import { useMemo } from 'react'

import { Table } from '@circlefin/components'
import { useDate } from '@circlefin/hooks'
import { UserStatusType } from '@shared/graphql'
import classNames from 'classnames'
import useTranslation from 'next-translate/useTranslation'

import { UsersTableApprovalLink } from './UsersTable.ApprovalStatus'
import { UsersTableInviteActions } from './UsersTable.InviteActions'
import { UsersTableMenu } from './UsersTable.Menu'

import type { User } from '@shared/graphql'

export interface UsersTableRowProps {
  /**
   * The user object for the row to represent.
   */
  user: User
}

export const UsersTableRow: React.FC<UsersTableRowProps> = ({ user }) => {
  const { t } = useTranslation('settings')
  const { date } = useDate()

  const { name, email, permissionGroup, status, lastLogin } = user

  const isActiveUser = useMemo(
    () =>
      status === UserStatusType.ACTIVE ||
      status === UserStatusType.PENDING_REMOVE_APPROVAL,
    [status],
  )

  return (
    <Table.Body.Row
      className={classNames({
        'bg-neutral': !isActiveUser,
      })}
      data-testid="user-table-row"
    >
      {/* User name and email */}
      <Table.Body.Cell className="text-neutral-strong type-body-sm">
        {name}
        <div className="text-neutral-subtlest">{email}</div>
      </Table.Body.Cell>

      {/* User role and Authorized Representative note */}
      <Table.Body.Cell className="text-neutral-strong type-body-sm">
        {permissionGroup != null &&
          t(`personalInfo.roleType.${permissionGroup}`)}
        {user.isAuthorizedRepresentative === true && (
          <div className="whitespace-nowrap text-neutral-subtlest">
            {t('personalInfo.authorizedRep')}
          </div>
        )}
      </Table.Body.Cell>

      {/* User status and last login */}
      <Table.Body.Cell className="text-neutral-strong type-body-sm">
        <div>{t(`personalInfo.status.${status}`)}</div>
        {lastLogin && (
          <div className="text-neutral-subtlest">
            {t('personalInfo.lastLogin', {
              date: date(lastLogin, 'date'),
            })}
          </div>
        )}
        {/* Invite actions for pending invite or expired invite */}
        <UsersTableInviteActions user={user} />
        <UsersTableApprovalLink user={user} />
      </Table.Body.Cell>

      {/* User action menu */}
      <Table.Body.Cell className="bg-transparent">
        <UsersTableMenu user={user} />
      </Table.Body.Cell>
    </Table.Body.Row>
  )
}
